/*
 * FOR SALE — FAQ (/pages/faq, section fs-info-faq) : amélioration progressive des questions (details/summary).
 *  - ancres stables : une adresse #<question> ouvre la question visée (au chargement et à chaque changement d'ancre),
 *    #theme-<…> mène au thème. Ces ancres sont citées dans les tickets Discord : ne jamais les renommer ;
 *  - recherche « Rechercher une question » : filtre en direct les intitulés et les réponses (sans accents ni casse ; mots
 *    de 3 lettres au moins, cherchés en début de mot),
 *    surligne les mots trouvés, ouvre les questions dont seule la réponse correspond, masque les thèmes vides, met à jour
 *    le nombre de questions par thème et la phrase d'état (role="status") ; Échap ou « Effacer la recherche » vident ;
 *  - « Tout ouvrir » / « Tout fermer » sur les questions affichées, sur la ligne d'état de la recherche ; au repos, la
 *    ligne n'affiche que ce bouton (le nombre de questions reste lu par les lecteurs d'écran) ;
 *  - icône « Copier le lien » en tête de chaque réponse (adresse de la question) : bulle « Copier le lien » au survol
 *    (300 ms) et au focus, coche et « Lien copié » pendant 2 s, annonce polie ; Échap ferme la bulle (WCAG 1.4.13) ;
 *  - ouverture instantanée quand c'est le script qui ouvre (ancre, recherche, « Tout ouvrir ») : la hauteur animée des
 *    questions (fs-motion.css, ::details-content) ne joue que pour un clic sur une question.
 * Textes dans la langue de la page (window.fsI18n, zone page.faq_ui de i18n/page.json) ; la recherche porte sur le texte
 * affiché, donc sur la langue active. Les ancres ne changent pas avec la langue.
 * Sans ce script : toutes les questions restent affichées, repliées ; la recherche et « Tout ouvrir » sont masqués.
 */
(() => {
  const root = document.querySelector('[data-fs-info-page]');
  const list = root?.querySelector('[data-fs-faq-list]');
  if (!root || !list) return;
  const questions = [...list.querySelectorAll('details[data-fs-info-q]')];
  const themes = [...list.querySelectorAll('[data-fs-faq-theme]')];
  const input = root.querySelector('[data-fs-faq-search]');
  const status = root.querySelector('[data-fs-faq-status]');
  const empty = root.querySelector('[data-fs-faq-empty]');
  const allButton = root.querySelector('[data-fs-faq-all]');
  const allLabel = root.querySelector('[data-fs-faq-all-label]');
  const total = questions.length;
  const t = (key, params) => window.fsI18n?.t(`page.faq_ui.${key}`, params) ?? key;

  /* ---- Ouverture instantanée : à appeler autour de toute ouverture ou fermeture faite par le script ---- */
  const instantly = (change) => {
    root.setAttribute('data-fs-faq-instant', '');
    change();
    requestAnimationFrame(() => requestAnimationFrame(() => root.removeAttribute('data-fs-faq-instant')));
  };

  /* ---- Ancre : ouvrir la question visée ---- */
  const openFromHash = ({ focus = true } = {}) => {
    let id = '';
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
    const target = id ? document.getElementById(id) : null;
    if (target?.matches?.('details[data-fs-info-q]')) {
      if (target.hasAttribute('data-fs-faq-hidden')) clearSearch();
      instantly(() => { target.open = true; });
      target.scrollIntoView({ block: 'start' });
      if (focus) target.querySelector('summary')?.focus({ preventScroll: true });
    }
  };

  /* ---- Copier le lien d'une question : icône discrète, confirmée par une coche et une bulle ---- */
  const ICON = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">'
    + '<g class="fs-copy-link"><path d="M8.5 11.5a3.2 3.2 0 0 0 4.5 0l2.6-2.6a3.2 3.2 0 0 0-4.5-4.5l-1 1"/>'
    + '<path d="M11.5 8.5a3.2 3.2 0 0 0-4.5 0l-2.6 2.6a3.2 3.2 0 0 0 4.5 4.5l1-1"/></g>'
    + '<path class="fs-copy-check" pathLength="1" d="M4.5 10.5l3.6 3.6L15.5 6.5"/></svg>';
  const live = document.createElement('p');
  live.className = 'fs-visually-hidden';
  live.setAttribute('role', 'status');
  root.append(live);
  questions.forEach((question) => {
    const body = question.querySelector('.fs-info-q-body');
    const title = question.querySelector('.fs-info-q-title');
    if (!body || !question.id) return;
    if (title && !title.id) title.id = `${question.id}-q`;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'fs-copy fs-info-q-copy';
    button.setAttribute('aria-label', t('copy_label'));
    if (title) button.setAttribute('aria-describedby', title.id);
    button.innerHTML = `${ICON}<span class="fs-copy-tip" aria-hidden="true"></span>`;
    const tip = button.querySelector('.fs-copy-tip');
    tip.textContent = t('copy');
    let timer = 0;
    const rest = () => {
      button.removeAttribute('data-copied');
      button.removeAttribute('data-copy-failed');
      tip.textContent = t('copy');
    };
    button.addEventListener('click', async () => {
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = question.id;
      window.clearTimeout(timer);
      button.removeAttribute('data-tip-off');
      try {
        await navigator.clipboard.writeText(url.toString());
        tip.textContent = t('copied');
        button.setAttribute('data-copied', '');
        live.textContent = t('copied_live');
      } catch {
        window.history.replaceState(window.history.state, '', `#${question.id}`);
        tip.textContent = t('in_bar');
        button.setAttribute('data-copy-failed', '');
        live.textContent = t('copy_failed');
      }
      timer = window.setTimeout(rest, 2000);
    });
    // Échap ferme la bulle jusqu'au prochain survol ou focus.
    button.addEventListener('keydown', (event) => { if (event.key === 'Escape') button.setAttribute('data-tip-off', ''); });
    ['pointerleave', 'blur'].forEach((type) => button.addEventListener(type, () => button.removeAttribute('data-tip-off')));
    body.prepend(button);
  });

  /* ---- Recherche ---- */
  // Sans accents ni casse, dans toutes les langues de la page (ё → е, й → и, ñ → n, İ → i) ; même longueur que le texte
  // pour les lettres latines et cyrilliques, ce qui permet de surligner au bon endroit.
  const fold = (text) => String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const letter = /[\p{L}\p{N}]/u;
  const index = questions.map((question) => ({
    question,
    title: fold(question.querySelector('.fs-info-q-title')?.textContent ?? ''),
    body: fold([...question.querySelectorAll('.fs-info-q-body p')].map((p) => p.textContent).join(' ')),
  }));
  const openedBySearch = new Set();
  // Un mot cherché correspond au début d'un mot du texte (« paie » trouve « paiement », pas « rapaiement »).
  const starts = (text, term) => {
    let at = text.indexOf(term);
    while (at !== -1) {
      if (at === 0 || !letter.test(text[at - 1])) return true;
      at = text.indexOf(term, at + 1);
    }
    return false;
  };

  const unmark = () => {
    list.querySelectorAll('mark.fs-info-hit').forEach((mark) => {
      const parent = mark.parentNode;
      mark.replaceWith(document.createTextNode(mark.textContent));
      parent.normalize();
    });
  };
  const markTerms = (element, terms) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (node.parentElement?.closest('button, mark') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const text = node.textContent;
      const folded = fold(text);
      // fold() garde la longueur des lettres latines et cyrilliques (accents retirés un par un) : les positions concordent.
      if (folded.length !== text.length) return;
      const ranges = [];
      terms.forEach((term) => {
        let at = folded.indexOf(term);
        while (at !== -1) {
          if (at === 0 || !letter.test(folded[at - 1])) ranges.push([at, at + term.length]);
          at = folded.indexOf(term, at + term.length);
        }
      });
      if (!ranges.length) return;
      ranges.sort((a, b) => a[0] - b[0]);
      const fragment = document.createDocumentFragment();
      let cursor = 0;
      ranges.forEach(([start, end]) => {
        if (start < cursor) return;
        fragment.append(text.slice(cursor, start));
        const mark = document.createElement('mark');
        mark.className = 'fs-info-hit';
        mark.textContent = text.slice(start, end);
        fragment.append(mark);
        cursor = end;
      });
      fragment.append(text.slice(cursor));
      node.replaceWith(fragment);
    });
  };

  const counts = [...root.querySelectorAll('[data-fs-faq-count], [data-fs-faq-jump-count]')];
  function search(raw) {
    const query = fold(raw.trim());
    const terms = query.split(/[\s'’-]+/).filter((term) => term.length >= 3);
    unmark();
    let shown = 0;
    instantly(() => {
      openedBySearch.forEach((question) => { question.open = false; });
      openedBySearch.clear();
      index.forEach(({ question, title, body }) => {
        const hit = !terms.length || terms.every((term) => starts(title, term) || starts(body, term));
        question.toggleAttribute('data-fs-faq-hidden', !hit);
        if (!hit) return;
        shown += 1;
        if (!terms.length) return;
        markTerms(question, terms);
        const inTitle = terms.every((term) => starts(title, term));
        if (!inTitle && !question.open) { question.open = true; openedBySearch.add(question); }
      });
    });
    themes.forEach((theme) => {
      const visible = theme.querySelectorAll('details[data-fs-info-q]:not([data-fs-faq-hidden])').length;
      theme.toggleAttribute('data-fs-faq-hidden', visible === 0);
      counts.filter((node) => (node.dataset.fsFaqCount ?? node.dataset.fsFaqJumpCount) === theme.id).forEach((count) => {
        count.firstChild.textContent = String(visible);
        count.closest('a')?.classList.toggle('is-empty', visible === 0);
      });
    });
    if (empty) empty.hidden = shown > 0;
    if (status) {
      status.textContent = !terms.length
        ? t('count', { count: total })
        : shown === 0 ? t('none')
          : t('matches', { count: shown, query: raw.trim() });
      status.classList.toggle('is-idle', !terms.length);
    }
    syncAll();
    root.dispatchEvent(new CustomEvent('fs:info-filter'));
  }
  function clearSearch() {
    if (!input) return;
    input.value = '';
    search('');
  }

  /* ---- Tout ouvrir / Tout fermer ---- */
  const visibleQuestions = () => questions.filter((question) => !question.hasAttribute('data-fs-faq-hidden'));
  function syncAll() {
    if (!allLabel) return;
    const shown = visibleQuestions();
    allLabel.textContent = shown.length && shown.every((question) => question.open) ? t('close_all') : t('open_all');
  }

  const searchBox = root.querySelector('[data-fs-faq-search-box]');
  if (searchBox) searchBox.hidden = false;
  // Phrase d'état écrite avant de devenir une zone annoncée : rien n'est lu au chargement, seulement après une recherche.
  if (status) {
    status.textContent = t('count', { count: total });
    status.classList.add('is-idle');
    window.setTimeout(() => status.setAttribute('role', 'status'), 0);
  }
  allButton?.removeAttribute('aria-pressed');
  allButton?.addEventListener('click', () => {
    const shown = visibleQuestions();
    const open = !shown.every((question) => question.open);
    instantly(() => shown.forEach((question) => { question.open = open; }));
    openedBySearch.clear();
    syncAll();
  });
  questions.forEach((question) => question.addEventListener('toggle', () => {
    if (!question.open) openedBySearch.delete(question);
    syncAll();
  }));
  let timer = 0;
  input?.addEventListener('input', () => { window.clearTimeout(timer); timer = window.setTimeout(() => search(input.value), 120); });
  input?.addEventListener('keydown', (event) => { if (event.key === 'Escape' && input.value) { event.preventDefault(); clearSearch(); } });
  root.querySelector('[data-fs-faq-clear]')?.addEventListener('click', () => { clearSearch(); input?.focus(); });

  // Au chargement : la question s'ouvre et le navigateur y défile, sans voler le focus ; ensuite, focus sur son intitulé.
  openFromHash({ focus: false });
  window.addEventListener('hashchange', () => openFromHash());
  syncAll();
})();
